import { CHAT_MAX_LENGTH, type ChatMessage, type PlayerInfo, type Presence } from '../../../shared/protocol';
import { playerHue } from '../multiplayer/ghosts';
import type { ConnectionState } from '../net/socket';

const TOAST_MS = 5000;
const MAX_TOASTS = 3;
const NEAR_BOTTOM_PX = 80;

export interface ChatCallbacks {
  send(text: string): boolean;
  loadMore(beforeId: number): void;
  describe(pos: Presence | null): string;
}

const CONNECTION_TEXT: Record<ConnectionState, string> = {
  connecting: 'Conectando…',
  online: '',
  offline: 'Sem conexão. Tentando de novo…',
  unauthorized: 'Abra o jogo pelo Telegram para entrar no chat.',
  forbidden: 'Seu acesso ainda não foi liberado. Os admins já foram avisados no bot.',
  replaced: 'Você abriu o jogo em outro aparelho ou aba.',
};

const $ = <T extends HTMLElement>(root: ParentNode, selector: string): T => {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Elemento não encontrado: ${selector}`);
  return element;
};

const time = (at: number) => new Date(at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

function dayLabel(at: number): string {
  const date = new Date(at);
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Hoje';
  if (date.toDateString() === yesterday.toDateString()) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Painel de chat (com histórico persistente), lista de quem está online e avisos na tela. */
export class ChatPanel {
  private readonly panel: HTMLElement;
  private readonly log: HTMLElement;
  private readonly list: HTMLOListElement;
  private readonly moreButton: HTMLButtonElement;
  private readonly onlineList: HTMLUListElement;
  private readonly form: HTMLFormElement;
  private readonly input: HTMLInputElement;
  private readonly stateLine: HTMLElement;
  private readonly badge: HTMLElement;
  private readonly onlineCount: HTMLElement;

  private messages = new Map<number, ChatMessage>();
  private hasMore = false;
  private loadingMore = false;
  private selfId: number | null = null;
  private selfName = '';
  private selfPresence: Presence | null = null;
  private players = new Map<number, PlayerInfo>();
  private unread = 0;
  private connection: ConnectionState = 'offline';

  constructor(
    root: HTMLElement,
    private readonly toggleButton: HTMLButtonElement,
    private readonly toasts: HTMLElement,
    private readonly callbacks: ChatCallbacks,
  ) {
    this.panel = root;
    this.log = $(root, '.chat-log');
    this.list = $(root, '.chat-list');
    this.moreButton = $(root, '.chat-more');
    this.onlineList = $(root, '.online-list');
    this.form = $(root, '.chat-form');
    this.input = $(root, '.chat-form input');
    this.stateLine = $(root, '.chat-state');
    this.badge = $(toggleButton, '.badge');
    this.onlineCount = $(root, '.online-count');
    this.input.maxLength = CHAT_MAX_LENGTH;

    toggleButton.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    $(root, '.chat-close').addEventListener('click', () => this.close());
    root.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((button) =>
      button.addEventListener('click', () => this.showTab(button.dataset.tab as 'chat' | 'online')),
    );
    this.moreButton.addEventListener('click', () => this.requestMore());
    this.log.addEventListener('scroll', () => {
      if (this.log.scrollTop < 40) this.requestMore();
    });

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const text = this.input.value.trim();
      if (!text) return;
      if (this.callbacks.send(text)) this.input.value = '';
    });
    this.input.addEventListener('keydown', (event) => {
      event.stopPropagation(); // não deixa a tecla chegar no emulador
      if (event.key === 'Escape') this.close();
    });

    // Teclado (desktop): T abre o chat, como nos MMOs.
    window.addEventListener('keydown', (event) => {
      if (this.isOpen || event.repeat || !this.toggleButton.offsetParent) return;
      if (event.code === 'KeyT') {
        event.preventDefault();
        this.open();
      }
    });
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.panel.hidden = false;
    document.body.classList.add('chat-open');
    this.unread = 0;
    this.renderBadge();
    this.toasts.replaceChildren();
    this.scrollToBottom();
    // No celular, focar abre o teclado na cara; só foca sozinho no desktop.
    if (matchMedia('(pointer: fine)').matches) this.input.focus();
  }

  close(): void {
    this.input.blur();
    this.panel.hidden = true;
    document.body.classList.remove('chat-open');
  }

  setSelf(id: number, name: string): void {
    this.selfId = id;
    this.selfName = name;
  }

  setSelfPresence(pos: Presence | null): void {
    this.selfPresence = pos;
  }

  setConnection(state: ConnectionState): void {
    this.connection = state;
    this.stateLine.textContent = CONNECTION_TEXT[state];
    this.stateLine.hidden = state === 'online';
    this.input.disabled = state !== 'online';
    this.toggleButton.classList.toggle('is-online', state === 'online');
  }

  /** Página inicial do histórico (ao conectar ou reconectar). */
  welcome(history: ChatMessage[], hasMore: boolean, players: PlayerInfo[]): void {
    const wasEmpty = this.messages.size === 0;
    for (const message of history) this.messages.set(message.id, message);
    if (wasEmpty) this.hasMore = hasMore;
    this.players = new Map(players.map((player) => [player.id, player]));
    this.renderMessages('bottom');
    this.renderPlayers();
  }

  add(message: ChatMessage): void {
    const stick = this.isNearBottom();
    this.messages.set(message.id, message);
    this.renderMessages(stick || message.userId === this.selfId ? 'bottom' : 'keep');
    if (this.isOpen || message.userId === this.selfId) return;
    this.unread++;
    this.renderBadge();
    this.toast(message.name, message.text, playerHue(message.userId));
  }

  older(messages: ChatMessage[], hasMore: boolean): void {
    this.loadingMore = false;
    this.hasMore = hasMore;
    for (const message of messages) this.messages.set(message.id, message);
    this.renderMessages('anchor');
  }

  notice(text: string): void {
    if (this.isOpen) {
      this.stateLine.textContent = text;
      this.stateLine.hidden = false;
      setTimeout(() => this.setConnection(this.connection), 3000);
    } else {
      this.toast(null, text);
    }
  }

  playerJoined(player: PlayerInfo): void {
    this.players.set(player.id, player);
    this.renderPlayers();
    if (!this.isOpen) this.toast(null, `${player.name} entrou no jogo`);
  }

  playerLeft(id: number): void {
    this.players.delete(id);
    this.renderPlayers();
  }

  playerMoved(id: number, pos: Presence): void {
    const player = this.players.get(id);
    if (!player) return;
    const changedPlace = player.pos?.map.join() !== pos.map.join() || player.pos?.scene !== pos.scene;
    player.pos = pos;
    if (changedPlace) this.renderPlayers();
  }

  /** Atualiza a nossa linha na lista (chamado quando mudamos de mapa/cena). */
  refreshPlayers(): void {
    this.renderPlayers();
  }

  private showTab(tab: 'chat' | 'online'): void {
    this.panel.querySelectorAll<HTMLElement>('[data-tab]').forEach((button) =>
      button.classList.toggle('on', button.dataset.tab === tab),
    );
    this.log.hidden = tab !== 'chat';
    this.onlineList.hidden = tab !== 'online';
    this.form.hidden = tab !== 'chat';
    if (tab === 'chat') this.scrollToBottom();
  }

  private requestMore(): void {
    if (!this.hasMore || this.loadingMore || this.connection !== 'online') return;
    const oldest = Math.min(...this.messages.keys());
    if (!Number.isFinite(oldest)) return;
    this.loadingMore = true;
    this.callbacks.loadMore(oldest);
  }

  private isNearBottom(): boolean {
    return this.log.scrollHeight - this.log.scrollTop - this.log.clientHeight < NEAR_BOTTOM_PX;
  }

  private scrollToBottom(): void {
    this.log.scrollTop = this.log.scrollHeight;
  }

  private renderMessages(scroll: 'bottom' | 'keep' | 'anchor'): void {
    const previousHeight = this.log.scrollHeight;
    const previousTop = this.log.scrollTop;
    const sorted = [...this.messages.values()].sort((a, b) => a.id - b.id);

    const items: HTMLElement[] = [];
    let lastDay = '';
    let lastUser: number | null = null;
    for (const message of sorted) {
      const day = dayLabel(message.at);
      if (day !== lastDay) {
        const separator = document.createElement('li');
        separator.className = 'day';
        separator.textContent = day;
        items.push(separator);
        lastDay = day;
        lastUser = null;
      }
      const item = document.createElement('li');
      const mine = message.userId === this.selfId;
      item.className = `msg${mine ? ' mine' : ''}`;
      if (message.userId !== lastUser) {
        const who = document.createElement('span');
        who.className = 'who';
        who.textContent = mine ? 'Você' : message.name;
        who.style.color = `hsl(${playerHue(message.userId)} 85% 72%)`;
        item.append(who);
      }
      const text = document.createElement('span');
      text.className = 'text';
      text.textContent = message.text;
      const stamp = document.createElement('time');
      stamp.textContent = time(message.at);
      item.append(text, stamp);
      items.push(item);
      lastUser = message.userId;
    }
    if (sorted.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'empty';
      empty.textContent = 'Nenhuma mensagem ainda. Diga oi! 👋';
      items.push(empty);
    }
    this.list.replaceChildren(...items);
    this.moreButton.hidden = !this.hasMore;

    if (scroll === 'bottom') this.scrollToBottom();
    else if (scroll === 'anchor') this.log.scrollTop = previousTop + (this.log.scrollHeight - previousHeight);
  }

  private renderPlayers(): void {
    const rows: { name: string; you: boolean; where: string; battle: boolean; hue: number }[] = [];
    if (this.selfId !== null) {
      rows.push({
        name: this.selfName,
        you: true,
        where: this.callbacks.describe(this.selfPresence),
        battle: this.selfPresence?.scene === 'battle',
        hue: playerHue(this.selfId),
      });
    }
    const others = [...this.players.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    for (const player of others) {
      rows.push({
        name: player.name,
        you: false,
        where: this.callbacks.describe(player.pos),
        battle: player.pos?.scene === 'battle',
        hue: playerHue(player.id),
      });
    }

    this.onlineCount.textContent = String(rows.length);
    this.onlineList.replaceChildren(
      ...rows.map((row) => {
        const item = document.createElement('li');
        const name = document.createElement('strong');
        name.textContent = row.you ? `${row.name} (você)` : row.name;
        name.style.color = `hsl(${row.hue} 85% 72%)`;
        const where = document.createElement('span');
        where.textContent = `${row.battle ? '⚔️ Em batalha · ' : ''}${row.where}`;
        item.append(name, where);
        return item;
      }),
    );
  }

  private renderBadge(): void {
    this.badge.hidden = this.unread === 0;
    this.badge.textContent = this.unread > 9 ? '9+' : String(this.unread);
  }

  private toast(name: string | null, text: string, hue = 45): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    if (name) {
      const who = document.createElement('strong');
      who.textContent = `${name}: `;
      who.style.color = `hsl(${hue} 85% 72%)`;
      toast.append(who);
    } else {
      toast.classList.add('system');
    }
    toast.append(text);
    this.toasts.append(toast);
    while (this.toasts.children.length > MAX_TOASTS) this.toasts.firstElementChild?.remove();
    setTimeout(() => toast.classList.add('out'), TOAST_MS);
    setTimeout(() => toast.remove(), TOAST_MS + 400);
  }
}
